import { useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'palette/Button';
import { PaletteConfigProvider } from 'palette/Config';
import { Icon } from 'palette/Icon';
import { Header } from 'palette/Typography';
import { useConfig } from '@common/Hooks/useConfig';
import { useTranslation } from '@common/Hooks/useTranslation';
import { Product } from '@common/Listing/types';
import { MyListsCard } from '@common/MyLists/containers/MyListsCard';
import { openFavorites } from '@common/MyLists/store/actions/MyListsActions';
import { ItemInteractionOrigin, ItemsParameters } from '@common/Product/AddToFavorites/types';
import { trackProductImpressions } from '@common/Tracking';
import { isBoozt } from '@helpers/env';
import FavoritesCounter from '../containers/FavoritesCounter';
import { getCardTranslations } from '../helpers/cardTranslations';

interface FavoritesProductsSimpleProps {
    products: Product[];
    maxProducts: number;
    itemsParameters: ItemsParameters;
    interactionOrigin: ItemInteractionOrigin;
}

function FavoritesProductsSimple({
    products,
    maxProducts = 6,
    itemsParameters,
    interactionOrigin,
}: FavoritesProductsSimpleProps) {
    const dispatch = useDispatch();
    const config = useConfig();
    const translate = useTranslation();

    const productsContainerRef = useRef<HTMLUListElement | null>(null);

    const { URLPrefix, SVGURL } = config;
    const favoritesUrl = `${URLPrefix}/customer/my-lists#list=favourites`;

    const openFavoritesClick = (event: React.MouseEvent) => {
        event.preventDefault();
        dispatch(openFavorites());
    };

    useEffect(() => {
        if (productsContainerRef.current && isBoozt()) {
            trackProductImpressions(productsContainerRef.current);
        }
    }, [productsContainerRef]);

    return (
        <PaletteConfigProvider
            device="desktop"
            svgPath={SVGURL}
        >
            <a
                className="product-tabs__title"
                data-href={favoritesUrl}
                data-id="my_list"
                href={favoritesUrl}
                onClick={openFavoritesClick}
            >
                <Header
                    color="strong"
                    level="6"
                >
                    {translate('customer.dash_favourites.label')}
                </Header>
            </a>
            <ul
                ref={productsContainerRef}
                className="product-tabs__list product-tabs__list--favourites"
                data-current-page={1}
                data-products-group={itemsParameters.item_list_name}
                data-products-per-page={maxProducts}
                data-total-products={products.length}
            >
                {products.length > 0 ? (
                    products
                        .slice(0, maxProducts)
                        .map((product, index) => {
                            const bmpTrackingData = {
                                totalProducts: products.length,
                                productsPerPage: products.length,
                                itemIndex: index,
                                group: itemsParameters.item_list_name,
                                containerOffsetWidth: productsContainerRef.current?.offsetWidth,
                            };

                            return (
                                <MyListsCard
                                    key={`${product?.variants?.[0].id}_favoriteViewedPDP`}
                                    cardStrings={getCardTranslations(translate)}
                                    containerClassName="product-tabs__product"
                                    product={product}
                                    trackingValues={{
                                        list: itemsParameters.item_list_name,
                                        bmp: bmpTrackingData,
                                        itemsParameters,
                                        interactionOrigin,
                                    }}
                                    type="recentlyViewedPDP"
                                />
                            );
                        })
                ) : (
                    <li className="message-empty">
                        <div className="message-empty__thumb">
                            <Icon
                                name="palette_heart"
                            />
                        </div>
                        {translate('customer_my_lists_favourites_empty')}
                    </li>
                )}
            </ul>
            {products.length > maxProducts
                && (
                    <Button
                        className="view-all"
                        size="small"
                        theme="secondary"
                        onClick={openFavoritesClick}
                    >
                        {translate('helper.view_all.label')}
                        {' '}
                        (
                        <FavoritesCounter />
                        )
                    </Button>
                )}
        </PaletteConfigProvider>
    );
}

export default FavoritesProductsSimple;
